import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Eye, EyeOff, ArrowLeft, ArrowRight, Mail, Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth, apiErr } from "./AuthContext";
import { api } from "./api";
import LoginScene from "./login/LoginScene";
import LoginBrand from "./login/LoginBrand";
import "./login/login.css";

const COPY = {
  login: { title: "Acesso administrativo", desc: "Entre com sua conta de administrador.", cta: "Entrar" },
  forgot: { title: "Recuperar acesso", desc: "Informe o e-mail da conta para ver como redefinir a senha.", cta: "Continuar" },
  reset: { title: "Nova senha", desc: "Use pelo menos 10 caracteres, com letras e números ou símbolos.", cta: "Salvar nova senha" },
};

export function Field({ id, label, icon: Icon, error, aside, children }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="lg-label">{label}</label>
        {aside}
      </div>
      <div className="lg-field" data-invalid={!!error || undefined}>
        <span className="lg-field-icon" aria-hidden="true"><Icon size={16} /></span>
        {children}
      </div>
      <AnimatePresence>
        {error && (
          <motion.p id={`${id}-err`} className="lg-err" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{error}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export function PasswordInput({ id, value, onChange, autoComplete, invalid, testid, onCaps }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete}
        data-testid={testid} aria-invalid={invalid || undefined} aria-describedby={invalid ? `${id}-err` : undefined}
        onKeyUp={(e) => onCaps?.(e.getModifierState && e.getModifierState("CapsLock"))} className="lg-input" />
      <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`} className="lg-eye"
        aria-label={show ? "Ocultar senha" : "Mostrar senha"}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </>
  );
}

export function Message({ kind, testid, children }) {
  const reduce = useReducedMotion();
  const Icon = kind === "err" ? AlertCircle : CheckCircle2;
  return (
    <motion.div role={kind === "err" ? "alert" : "status"} data-testid={testid} className={`lg-msg lg-msg-${kind}`}
      initial={{ opacity: 0, x: reduce || kind !== "err" ? 0 : -8 }}
      animate={{ opacity: 1, x: reduce || kind !== "err" ? 0 : [-8, 6, -3, 0] }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
      <Icon size={16} aria-hidden="true" />
      <span>{children}</span>
    </motion.div>
  );
}

function useLoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [f, setF] = useState({ email: "", password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [caps, setCaps] = useState(false);

  useEffect(() => { if (resetToken) setMode("reset"); }, [resetToken]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const switchMode = (m) => {
    setMode(m); setErrors({}); setFormError(""); setNotice(""); setF((p) => ({ ...p, password: "", confirm: "" }));
    if (m !== "reset" && resetToken) setParams({});
  };

  const validate = () => {
    const e = {};
    if (mode !== "reset" && !/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Informe um e-mail válido.";
    if (mode === "login" && !f.password) e.password = "Informe a senha.";
    if (mode === "reset") {
      if (f.password.length < 10) e.password = "Use pelo menos 10 caracteres.";
      else if (f.password !== f.confirm) e.confirm = "As senhas não coincidem.";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setFormError(""); setNotice("");
    if (loading || !validate()) return;
    setLoading(true);
    try {
      if (mode === "login") {
        await login(f.email.trim(), f.password);
        navigate("/admin", { replace: true });
        return;
      }
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: f.email.trim() });
        setNotice(data.message);
      } else {
        await api.post("/auth/reset-password", { token: resetToken, password: f.password });
        switchMode("login");
        setNotice("Senha redefinida. Entre com a nova senha.");
      }
    } catch (err) {
      setFormError(err?.response?.status === 401 ? "E-mail ou senha incorretos." : apiErr(err));
      if (mode === "login") setF((p) => ({ ...p, password: "" }));
    } finally {
      setLoading(false);
    }
  };

  return { mode, f, set, errors, formError, notice, loading, caps, setCaps, switchMode, onSubmit };
}

function LoginForm() {
  const reduce = useReducedMotion();
  const { mode, f, set, errors, formError, notice, loading, caps, setCaps, switchMode, onSubmit } = useLoginForm();
  const copy = COPY[mode];

  return (
    <motion.div className="lg-form-wrap" initial={{ opacity: 0, x: reduce ? 0 : 28 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}>
      {mode !== "login" ? (
        <button type="button" onClick={() => switchMode("login")} data-testid="back-to-login" className="lg-link inline-flex items-center gap-1.5">
          <ArrowLeft size={13} /> Voltar para o login
        </button>
      ) : <span className="lg-tag">Área restrita</span>}

      <AnimatePresence mode="wait">
        <motion.div key={mode} initial={{ opacity: 0, y: reduce ? 0 : 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduce ? 0 : -6 }} transition={{ duration: 0.3 }}>
          <h1 id="login-title" className="lg-title" data-testid="login-title">{copy.title}</h1>
          <p className="lg-desc">{copy.desc}</p>

          <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid={`form-${mode}`} aria-labelledby="login-title">
            {mode !== "reset" && (
              <Field id="email" label="E-mail" icon={Mail} error={errors.email}>
                <input id="email" type="email" inputMode="email" autoComplete="username" autoFocus value={f.email} onChange={set("email")}
                  data-testid="login-email" aria-invalid={!!errors.email || undefined} aria-describedby={errors.email ? "email-err" : undefined} className="lg-input" />
              </Field>
            )}
            {mode === "login" && (
              <Field id="password" label="Senha" icon={Lock} error={errors.password}
                aside={<button type="button" onClick={() => switchMode("forgot")} data-testid="forgot-link" className="lg-link mb-2">Esqueci minha senha</button>}>
                <PasswordInput id="password" value={f.password} onChange={set("password")} autoComplete="current-password" invalid={!!errors.password} testid="login-password" onCaps={setCaps} />
              </Field>
            )}
            {mode === "reset" && (
              <>
                <Field id="new-password" label="Nova senha" icon={Lock} error={errors.password}>
                  <PasswordInput id="new-password" value={f.password} onChange={set("password")} autoComplete="new-password" invalid={!!errors.password} testid="reset-password" onCaps={setCaps} />
                </Field>
                <Field id="confirm-password" label="Confirmar senha" icon={Lock} error={errors.confirm}>
                  <PasswordInput id="confirm-password" value={f.confirm} onChange={set("confirm")} autoComplete="new-password" invalid={!!errors.confirm} testid="reset-confirm" onCaps={setCaps} />
                </Field>
              </>
            )}

            {caps && mode !== "forgot" && <p className="lg-caps" data-testid="caps-warning">Caps Lock está ativado.</p>}
            <AnimatePresence>
              {formError && <Message kind="err" testid="login-error" key="err">{formError}</Message>}
              {notice && <Message kind="ok" testid="login-notice" key="ok">{notice}</Message>}
            </AnimatePresence>

            <button type="submit" data-testid="login-submit" disabled={loading} aria-busy={loading} className="lg-btn">
              {loading ? <><span className="lg-spin" aria-hidden="true" /> {mode === "login" ? "Verificando…" : "Enviando…"}</>
                : <>{copy.cta} <ArrowRight size={17} className="lg-btn-arrow" aria-hidden="true" /></>}
            </button>
          </form>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

export default function Login() {
  return (
    <div className="mia-admin lg-root" data-testid="login-page">
      <LoginScene />
      <header className="lg-top">
        <span />
        <Link to="/" className="lg-back" data-testid="login-back-site"><ArrowLeft size={12} /> Voltar ao site</Link>
      </header>
      <main className="lg-layout">
        <LoginBrand />
        <section className="lg-dock" aria-label="Formulário de acesso">
          <span className="lg-beam" aria-hidden="true" />
          <LoginForm />
        </section>
      </main>
    </div>
  );
}
